import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [reminders, setReminders] = useState([]);
  const [isSupported, setIsSupported] = useState(false);

  // Check if notifications are supported
  useEffect(() => {
    setIsSupported('Notification' in window);
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Load reminders from localStorage
  useEffect(() => {
    if (user) {
      const saved = localStorage.getItem(`reminders_${user.id}`);
      if (saved) {
        setReminders(JSON.parse(saved));
      }
    }
  }, [user]);

  // Save reminders to localStorage
  useEffect(() => {
    if (user && reminders.length > 0) {
      localStorage.setItem(`reminders_${user.id}`, JSON.stringify(reminders));
    }
  }, [reminders, user]);

  // Request notification permission
  const requestPermission = async () => {
    if (!isSupported) return false;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  // Send a notification
  const sendNotification = useCallback((title, options = {}) => {
    if (!isSupported || permission !== 'granted') return;

    const notification = new Notification(title, {
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: options.tag || 'calm-pathways',
      requireInteraction: options.requireInteraction || false,
      ...options,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) options.onClick();
    };

    return notification;
  }, [isSupported, permission]);

  // Add a reminder
  const addReminder = (reminder) => {
    const newReminder = {
      id: Date.now().toString(),
      ...reminder,
      enabled: true,
      createdAt: new Date().toISOString(),
    };
    setReminders(prev => [...prev, newReminder]);
    return newReminder;
  };

  // Remove a reminder
  const removeReminder = (id) => {
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  // Toggle reminder
  const toggleReminder = (id) => {
    setReminders(prev => prev.map(r => 
      r.id === id ? { ...r, enabled: !r.enabled } : r
    ));
  };

  // Update a reminder
  const updateReminder = useCallback((id, updates) => {
    setReminders(prev => prev.map(r => 
      r.id === id ? { ...r, ...updates } : r
    ));
  }, []);

  // Check and send due reminders
  const checkReminders = useCallback(() => {
    if (permission !== 'granted') return;

    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

    reminders.forEach(reminder => {
      if (!reminder.enabled) return;
      
      // Check if it's the right time
      if (reminder.time === currentTime) {
        // Check if it's the right day (for daily reminders, always true)
        const shouldSend = reminder.frequency === 'daily' || 
          (reminder.frequency === 'weekly' && reminder.days?.includes(currentDay)) ||
          reminder.frequency === 'once';

        if (shouldSend) {
          sendNotification(reminder.title, {
            body: reminder.body || `Time for: ${reminder.title}`,
            tag: `reminder-${reminder.id}`,
            requireInteraction: true,
          });

          // Disable one-time reminders after sending
          if (reminder.frequency === 'once') {
            updateReminder(reminder.id, { enabled: false });
          }
        }
      }
    });
  }, [reminders, permission, sendNotification, updateReminder]);

  // Set up reminder checking interval
  useEffect(() => {
    if (permission !== 'granted' || reminders.length === 0) return;

    // Check every minute
    const interval = setInterval(checkReminders, 60000);
    
    // Also check immediately
    checkReminders();

    return () => clearInterval(interval);
  }, [permission, reminders, checkReminders]);

  return (
    <NotificationContext.Provider value={{
      isSupported,
      permission,
      requestPermission,
      sendNotification,
      reminders,
      addReminder,
      removeReminder,
      toggleReminder,
      updateReminder,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
